import http, { httpMultipart } from "./http";

export interface ServicePayload {
  service_type: string;
  name: string;
  description?: string | null;
  thumbnail_file?: File | null;
  category?: string | null;
  status: string;
  is_visible_to_client?: boolean;
  is_onboarding_checklist_enabled?: boolean;
}

export interface ServiceRecord {
  id: number;
  service_type?: string;
  name: string;
  description?: string | null;
  thumbnail_file?: string | null;
  thumbnail_url?: string | null;
  category?: string | null;
  status: string;
  is_visible_to_client: boolean;
  is_onboarding_checklist_enabled: boolean;
  default_tasks?: Array<{
    id: number;
    service_id: number;
    title: string;
    instructions?: string | null;
    status: "active" | "inactive";
    sort_order: number;
  }>;
  created_at?: string;
  updated_at?: string;
}

function normalizeServiceRecord(service: any): ServiceRecord {
  const defaultTasks = Array.isArray(service?.default_tasks)
    ? service.default_tasks
    : Array.isArray(service?.tasks)
      ? service.tasks
      : [];

  return {
    ...service,
    default_tasks: defaultTasks,
  };
}

export const getServices = async () => {
  const response = await http.get("/api/services");
  return {
    ...response.data,
    data: Array.isArray(response.data?.data)
      ? response.data.data.map(normalizeServiceRecord)
      : [],
  };
};

export const getServiceById = async (id: string | number) => {
  const response = await http.get(`/api/services/${id}`);
  return normalizeServiceRecord(response.data);
};

export const createService = async (payload: ServicePayload) => {
  const response = payload.thumbnail_file
    ? await httpMultipart.post("/api/services", buildServiceFormData(payload))
    : await http.post("/api/services", payload);
  return response.data;
};

export const updateService = async (
  id: string | number,
  payload: ServicePayload
) => {
  const response = payload.thumbnail_file
    ? await httpMultipart.post(
        `/api/services/${id}`,
        buildServiceFormData(payload, "PUT")
      )
    : await http.put(`/api/services/${id}`, payload);
  return response.data;
};

export const deleteService = async (id: string | number) => {
  const response = await http.delete(`/api/services/${id}`);
  return response.data;
};

export const archiveService = async (service: ServiceRecord) => {
  const payload: ServicePayload = {
    service_type: service.service_type || "core",
    name: service.name,
    description: service.description || "",
    category: service.category || "",
    status: "archived",
    is_visible_to_client: service.is_visible_to_client,
    is_onboarding_checklist_enabled: service.is_onboarding_checklist_enabled,
  };
  const response = await http.put(`/api/services/${service.id}`, payload);
  return response.data;
};

export const restoreService = async (service: ServiceRecord) => {
  const payload: ServicePayload = {
    service_type: service.service_type || "core",
    name: service.name,
    description: service.description || "",
    category: service.category || "",
    status: "active",
    is_visible_to_client: service.is_visible_to_client,
    is_onboarding_checklist_enabled: service.is_onboarding_checklist_enabled,
  };
  const response = await http.put(`/api/services/${service.id}`, payload);
  return response.data;
};

function buildServiceFormData(payload: ServicePayload, method?: "PUT") {
  const formData = new FormData();

  if (method) {
    formData.append("_method", method);
  }

  formData.append("service_type", payload.service_type);
  formData.append("name", payload.name);
  formData.append("description", payload.description || "");
  formData.append("category", payload.category || "");
  formData.append("status", payload.status);
  formData.append(
    "is_visible_to_client",
    payload.is_visible_to_client ? "1" : "0"
  );
  formData.append(
    "is_onboarding_checklist_enabled",
    payload.is_onboarding_checklist_enabled ? "1" : "0"
  );

  if (payload.thumbnail_file) {
    formData.append("thumbnail_file", payload.thumbnail_file);
  }

  return formData;
}
